import { createContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Modal from "../common/Modal";
import DropdownMenu from "../common/DropdownMenu";
import NavDrawer from "./NavDrawer";
import ChangePasswordModal from "../common/ChangePasswordModal";
import DeleteAccountModal from "../common/DeleteAccountModal";
import { useAuthStore } from "../../redux/features/authSlice";
import Logo from "../../assets/svg/AI.Logo.svg";
import { useTranslation } from "react-i18next";

export const ModalContext = createContext();

const Header = () => {
  let { isLoggedin } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { t, i18n } = useTranslation(); // ✅ use i18n

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleScroll = () => {
    navigate(i18n.language === "en" ? "/en" : "/");
    setTimeout(() => {
      const worksSection = document.getElementById("how-it-works");
      if (worksSection) {
        worksSection.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const handleScrollCalculator = () => {
    navigate(i18n.language === "en" ? "/en" : "/");
    setTimeout(() => {
      const worksSection = document.getElementById("calculator");
      if (worksSection) {
        worksSection.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  useEffect(() => {
    if (token) {
      setIsModalOpen(true);
    }
  }, [token]);

  const OnCloseFun = (setCurrentComponent, check) => {
    navigate(window.location.pathname, { replace: true });
    if (check) {
      setCurrentComponent(check);
    }
    setIsModalOpen(false);
  };

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    if (newLang === i18n.language) return;
    i18n.changeLanguage(newLang);
    // Change URL path
    const pathParts = location.pathname.split("/").filter(Boolean);
    if (newLang === "en") {
      if (pathParts[0] !== "en") {
        navigate("/en" + location.pathname, { replace: true });
      }
    } else {
      if (pathParts[0] === "en") {
        // Remove 'en' from path
        const newPath = "/" + pathParts.slice(1).join("/");
        navigate(newPath === "/" ? "/" : newPath, { replace: true });
      }
    }
  };

  return (
    <ModalContext.Provider
      value={{
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        isChangePasswordModalOpen,
        setIsChangePasswordModalOpen,
      }}
    >
      <div className="container mx-auto max-w-7xl px-2 sm:px-4">
        <header className="mt-4 lg:mx-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 w-full  ">
          <div className="flex items-center min-w-0 flex-shrink-0 w-full lg:w-auto">
            <button
              className="lg:hidden py-1 mr-2"
              onClick={toggleMenu}
              aria-label="Open menu"
            >
              <svg
                className="w-8 h-8 sm:w-9 sm:h-9"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  className="text-red-600"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                ></path>
              </svg>
            </button>
            <Link
              to={i18n.language === "en" ? "/en" : "/"}
              className="flex items-center min-w-0"
            >
              <img
                src={Logo}
                alt="OfficeFinder Logo"
                className="h-8 md:h-12 w-auto object-contain transition-all"
              />
            </Link>
            {/* Mobile view */}
            {isLoggedin ? (
              <div className="lg:hidden block ml-auto">
                <DropdownMenu />
              </div>
            ) : (
              <button
                onClick={() => setIsModalOpen(true)}
                className="text-white lg:hidden block font-medium bg-red-600 py-2 px-6 sm:px-8 rounded-[58px] cursor-pointer ml-auto"
              >
                {t("signin")}
              </button>
            )}
          </div>

          <nav className="flex-1 hidden lg:flex justify-end min-w-0">
            <ul className="flex flex-wrap gap-x-2 xl:gap-x-8 items-center min-w-0">
              <li className="mr-0 my-2 lg:my-0 whitespace-nowrap">
                <p
                  onClick={handleScroll}
                  className="text-primary-text font-medium hover:text-cyan-900 cursor-pointer"
                >
                  {t("how_it_works")}
                </p>
              </li>
              <li className="whitespace-nowrap">
                <Link
                  className="text-primary-text font-medium hover:text-cyan-900"
                  to={i18n.language === "en" ? "/en/contact-us" : "/contact-us"}
                >
                  {t("contact")}
                </Link>
              </li>
              <li className="whitespace-nowrap">
                <div className="border rounded-[58px] py-2 px-8 border-[#C0C0C0]">
                  <p
                    onClick={handleScrollCalculator}
                    className="text-primary-text font-medium hover:text-cyan-900 cursor-pointer"
                  >
                    {t("find_office")}
                  </p>
                </div>
              </li>

              {/* 🔁 Language Switcher */}
              <li className="relative whitespace-nowrap min-w-[140px]">
                <select
                  value={i18n.language}
                  onChange={handleLanguageChange}
                  className="
                    border
                    rounded-[58px]
                    py-2
                    px-6
                    pr-10
                    min-w-[140px]
                    cursor-pointer
                    text-primary-text
                    font-medium
                    appearance-none
                    bg-white
                    text-center
                    focus:outline-none
                    focus:ring-2
                    focus:ring-red-500
                    focus:border-red-500
                    transition
                    duration-150
                    ease-in-out
                    shadow-sm
                    hover:border-red-400
                    hover:shadow-md
                    w-full
                  "
                  style={{ boxShadow: "0 2px 8px 0 rgba(0,0,0,0.06)" }}
                >
                  <option
                    value="en"
                    className="bg-white text-cyan-900 hover:bg-red-100"
                  >
                    English
                  </option>
                  <option
                    value="ro"
                    className="bg-white text-cyan-900 hover:bg-red-100"
                  >
                    română
                  </option>
                </select>

                {/* Custom arrow icon */}
                <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                  <svg
                    className="w-4 h-4 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </li>

              {isLoggedin ? (
                <li>
                  <DropdownMenu />
                </li>
              ) : (
                <li>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="text-white font-medium bg-red-600 py-2 px-8 rounded-[58px] cursor-pointer"
                  >
                    {t("signin")}
                  </button>
                </li>
              )}
            </ul>
          </nav>

          <NavDrawer
            isOpen={isOpen}
            toggleMenu={() => setIsOpen(false)}
            setIsModalOpen={setIsModalOpen}
          />
        </header>

        <Modal
          isOpen={isModalOpen}
          onClose={(setCurrentComponent, check) =>
            OnCloseFun(setCurrentComponent, check)
          }
        />
        <ChangePasswordModal
          isOpen={isChangePasswordModalOpen}
          onClose={() => setIsChangePasswordModalOpen(false)}
        />
        <DeleteAccountModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
        />
      </div>
    </ModalContext.Provider>
  );
};

export default Header;
